import * as THREE from 'three';
import type { SceneEffectInstance, SceneEffectType } from '../../../types/sceneEffects';

export type EffectManager = {
  root: THREE.Object3D;
  update: (elapsed: number) => void;
  dispose: () => void;
};

function clamp01(value: number | undefined, fallback: number): number {
  const n = typeof value === 'number' ? value : fallback;
  return Math.min(1, Math.max(0, n));
}

function disposeMaterial(material: THREE.Material | THREE.Material[]): void {
  if (Array.isArray(material)) {
    material.forEach((item) => item.dispose());
    return;
  }
  material.dispose();
}

function disposeAttached(object: THREE.Object3D): void {
  object.removeFromParent();
  if (object instanceof THREE.Mesh || object instanceof THREE.Points) {
    object.geometry.dispose();
    disposeMaterial(object.material);
  }
}

function createFullscreenPlane(
  parent: THREE.Object3D,
  width: number,
  height: number,
  material: THREE.Material
): THREE.Mesh {
  const geometry = new THREE.PlaneGeometry(width, height);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.z = 0;
  parent.add(mesh);
  return mesh;
}

function createPointsEffect(
  parent: THREE.Object3D,
  width: number,
  height: number,
  count: number,
  color: number,
  size: number,
  opacity: number
): { points: THREE.Points; geometry: THREE.BufferGeometry; velocities: Float32Array } {
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(count * 3);
  const velocities = new Float32Array(count * 3);

  for (let i = 0; i < count; i += 1) {
    const index = i * 3;
    positions[index] = (Math.random() - 0.5) * width;
    positions[index + 1] = (Math.random() - 0.5) * height;
    positions[index + 2] = Math.random() * 2;
    velocities[index] = (Math.random() - 0.5) * 0.4;
    velocities[index + 1] = -(0.4 + Math.random() * 0.8);
    velocities[index + 2] = 0;
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const material = new THREE.PointsMaterial({
    color,
    size,
    transparent: true,
    opacity,
    sizeAttenuation: false,
  });
  const points = new THREE.Points(geometry, material);
  parent.add(points);
  return { points, geometry, velocities };
}

function wrapY(positions: Float32Array, index: number, height: number): void {
  if (positions[index + 1] < -height / 2) positions[index + 1] = height / 2;
  if (positions[index + 1] > height / 2) positions[index + 1] = -height / 2;
}

function wrapX(positions: Float32Array, index: number, width: number): void {
  if (positions[index] < -width / 2) positions[index] = width / 2;
  if (positions[index] > width / 2) positions[index] = -width / 2;
}

export function createEffectManager(
  effect: SceneEffectInstance,
  parent: THREE.Object3D,
  width: number,
  height: number
): EffectManager | null {
  const params = effect.properties;
  const intensity = clamp01(params.intensity, 0.5);
  const speed = params.speed ?? 1;

  switch (effect.effectType as SceneEffectType) {
    case 'rain': {
      const count = Math.floor(120 + intensity * 280);
      const size = params.particleSize ?? 1.2;
      const { points, geometry, velocities } = createPointsEffect(
        parent,
        width,
        height,
        count,
        0xaec2e0,
        size,
        0.35 + intensity * 0.45
      );
      return {
        root: points,
        update: () => {
          const positions = geometry.attributes.position.array as Float32Array;
          for (let i = 0; i < count; i += 1) {
            const index = i * 3;
            positions[index] += velocities[index] * speed * 2.5;
            positions[index + 1] += velocities[index + 1] * speed * 8;
            wrapY(positions, index, height);
            wrapX(positions, index, width);
          }
          geometry.attributes.position.needsUpdate = true;
        },
        dispose: () => disposeAttached(points),
      };
    }
    case 'snow': {
      const count = Math.floor(80 + intensity * 180);
      const size = params.particleSize ?? 1.4;
      const { points, geometry, velocities } = createPointsEffect(
        parent,
        width,
        height,
        count,
        0xffffff,
        size,
        0.5 + intensity * 0.4
      );
      return {
        root: points,
        update: () => {
          const positions = geometry.attributes.position.array as Float32Array;
          for (let i = 0; i < count; i += 1) {
            const index = i * 3;
            positions[index] += velocities[index] * speed * 0.6;
            positions[index + 1] += velocities[index + 1] * speed * 1.2;
            wrapY(positions, index, height);
            wrapX(positions, index, width);
          }
          geometry.attributes.position.needsUpdate = true;
        },
        dispose: () => disposeAttached(points),
      };
    }
    case 'particles': {
      const count = Math.floor(60 + intensity * 160);
      const size = params.particleSize ?? 1.15;
      const color = new THREE.Color(params.color ?? '#ffdc78');
      const { points, geometry, velocities } = createPointsEffect(
        parent,
        width,
        height,
        count,
        color.getHex(),
        size,
        0.4 + intensity * 0.5
      );
      return {
        root: points,
        update: () => {
          const positions = geometry.attributes.position.array as Float32Array;
          for (let i = 0; i < count; i += 1) {
            const index = i * 3;
            positions[index] += velocities[index] * speed * 0.8;
            positions[index + 1] += velocities[index + 1] * speed * 0.5;
            wrapX(positions, index, width);
            wrapY(positions, index, height);
          }
          geometry.attributes.position.needsUpdate = true;
        },
        dispose: () => disposeAttached(points),
      };
    }
    case 'fog': {
      const material = new THREE.MeshBasicMaterial({
        color: 0xc8d2dc,
        transparent: true,
        opacity: 0.15 + intensity * 0.45,
        depthWrite: false,
      });
      const mesh = createFullscreenPlane(parent, width, height, material);
      return {
        root: mesh,
        update: () => {
          material.opacity = 0.15 + intensity * 0.45 + Math.sin(Date.now() * 0.0004 * speed) * 0.03;
        },
        dispose: () => disposeAttached(mesh),
      };
    }
    case 'lightning': {
      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      const mesh = createFullscreenPlane(parent, width, height, material);
      let flashTimer = 0;
      let flashDuration = 0;
      return {
        root: mesh,
        update: (elapsed) => {
          flashTimer -= elapsed;
          if (flashTimer <= 0 && Math.random() < 0.002 * speed) {
            flashTimer = 0.08 + Math.random() * 0.12;
            flashDuration = flashTimer;
          }
          if (flashTimer > 0) {
            material.opacity = intensity * (flashTimer / Math.max(flashDuration, 0.001)) * 0.8;
          } else {
            material.opacity = 0;
          }
        },
        dispose: () => disposeAttached(mesh),
      };
    }
    case 'vignette': {
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const gradient = ctx.createRadialGradient(128, 128, 40, 128, 128, 128);
        gradient.addColorStop(0, 'rgba(0,0,0,0)');
        gradient.addColorStop(1, `rgba(0,0,0,${0.25 + intensity * 0.55})`);
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 256, 256);
      }
      const texture = new THREE.CanvasTexture(canvas);
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
      });
      const mesh = createFullscreenPlane(parent, width, height, material);
      return {
        root: mesh,
        update: () => {},
        dispose: () => {
          texture.dispose();
          disposeAttached(mesh);
        },
      };
    }
    default:
      return null;
  }
}
